import { array } from 'fp-ts';

import { LocalizedText } from '@mews/framework/localization';
import { useToggle } from '@mews/framework/utils';
import { TextKey } from '@mews-distributor/common';
import { Clickable, ExpandIcon, Stack, StackItem, Typography } from '@optimus-web/core';
import { ExpandableBox } from '@optimus-web/helpers';

import { LocalizedTaxValue, mapTaxValues } from './mapTaxValues';

interface TaxBreakDownProps {
    taxTotal: string;
    taxValues: LocalizedTaxValue[];
    hasNetPricing: boolean;
}

export const TaxBreakDown = ({ taxTotal, taxValues, hasNetPricing }: TaxBreakDownProps) => {
    const [expanded, toggleExpanded] = useToggle({ initialValue: true });
    const taxValueRows = mapTaxValues(taxValues);
    const taxesNoteLabel = !hasNetPricing ? (
        <Typography textStyle="bodySmallStrong" variant="secondary" align="right" as="div">
            <LocalizedText textKey={TextKey.TaxPluralIncludedInPrice} />
        </Typography>
    ) : null;

    const groupedTaxValueRows = (
        <StackItem>
            <Stack spacing="200" distribution="space-between">
                <Clickable onClick={toggleExpanded} data-test-id="tax-breakdown-toggle-expandable-box" type="button">
                    <Stack spacing="200" distribution="space-between">
                        <Typography textStyle="bodyMediumStrong" as="div">
                            <LocalizedText textKey={TextKey.TaxPlural} />
                            <ExpandIcon expanded={expanded} />
                        </Typography>
                    </Stack>
                </Clickable>
                <Typography textStyle="bodyMediumStrong" as="div">
                    {taxTotal}
                </Typography>
            </Stack>
            <ExpandableBox expanded={expanded}>{taxValueRows}</ExpandableBox>
        </StackItem>
    );

    return (
        <Stack vertical spacing="50">
            <Stack spacing="100" vertical>
                {array.size(taxValues) === 1 ? taxValueRows : groupedTaxValueRows}
            </Stack>
            {taxesNoteLabel}
        </Stack>
    );
};
